import type { MaybeRefOrGetter } from 'vue';
import type { QuerySort } from '#layers/collection-query/types/rstore';
import type { FieldSelection, Filter, GlobalCollectionId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { refThrottled } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, toValue, watch } from 'vue';
import { useProcessedFieldSelection } from '#layers/access/public';
import { isDeepEqual, useAutoRetry } from '#layers/base/public';
import { useDynamicVariableFilter } from '#layers/collection-query/composables/use-dynamic-variable-filter';
import { extractPrimitiveFilterFieldNames, isRelationalFilter, usePaginatedQuery } from '#layers/data-model/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { DEFAULT_LISTING_PAGE_SIZE } from '#layers/ui/public';
import { useWorkspaceSchemaStore, useWorkspaceStore } from '#layers/workspace/public';

export interface ReadManyQuery extends FieldSelection {
	filter?: Filter;
	sort?: QuerySort;
	limit?: number;
	offset?: number;
}

interface UseItemsQuery extends ReadManyQuery {
	search?: string;
}

interface UseItemsOptions {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | GlobalCollectionId | undefined>;
	query?: MaybeRefOrGetter<UseItemsQuery>;
	/**
	 * Indicates if the query should run.
	 *
	 * TODO understand if this should also disable the updating of the items composable
	 * 		in the current state `items` might return an old value, and still react to
	 * 		changes in the filter and collection.
	 *
	 * @default true
	 */
	enabled?: MaybeRefOrGetter<boolean>;
	/**
	 * Page size override, if not provided the collection defaultPageSize is used
	 */
	pageSize?: MaybeRefOrGetter<number | undefined>;
	/**
	 * When true, sets a permission error if explicitly requested fields are not permitted.
	 * When false (default), non-permitted fields are silently excluded.
	 */
	errorOnNonPermittedField?: MaybeRefOrGetter<boolean>;
	/**
	 * Throttle network fetches while the query changes rapidly (e.g. typing a filter value),
	 * relying on client-side cache filtering in between.
	 *
	 * Disable for layouts whose query only changes in discrete steps (e.g. the calendar's
	 * view/range switches), where throttling adds latency without coalescing anything.
	 *
	 * @default true
	 */
	throttle?: MaybeRefOrGetter<boolean>;
}

export function useItems(options: UseItemsOptions) {
	const { collection, query, pageSize, enabled = true, errorOnNonPermittedField = false, throttle = true } = options;

	const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
	const { findCollectionById } = useWorkspaceSchemaStore();
	const store = useWorkspaceEntityStore();

	const computedCollection = computed(() => {
		const collectionOrId = toValue(collection);

		if (typeof collectionOrId === 'string') {
			return findCollectionById(collectionOrId);
		}
		else {
			return collectionOrId;
		}
	});

	const computedQuery = computed(() => toValue(query));
	const throttledQuery = refThrottled(computed(() => toValue(query)), 500);

	const { filter: resolvedFilter, ready: filterReady } = useDynamicVariableFilter(() => computedQuery.value?.filter);

	const computedEnabled = computed(() => toValue(enabled));

	const selectionWithFilterFields = computed<FieldSelection>(() => {
		const selection = {
			fields: computedQuery.value?.fields ?? ['*'],
			include: computedQuery.value?.include,
		};

		if (selection.fields.includes('*')) return selection;

		const filter = computedQuery.value?.filter;
		if (!filter) return selection;

		const filterFieldNames = extractPrimitiveFilterFieldNames(filter);
		if (filterFieldNames.length === 0) return selection;

		const merged = new Set(selection.fields);
		const sizeBefore = merged.size;

		for (const name of filterFieldNames) {
			merged.add(name);
		}

		if (merged.size === sizeBefore) return selection;
		return { ...selection, fields: [...merged] };
	});

	const { selection: processedSelection, excluded } = useProcessedFieldSelection({
		collection: computedCollection,
		selection: selectionWithFilterFields,
	});

	const permissionError = computed(() => {
		if (!toValue(errorOnNonPermittedField)) return null;
		if (excluded.value.length === 0) return null;
		return new Error(`Fields not permitted: ${excluded.value.join(', ')}`);
	});

	const computedPageSize = computed(() => toValue(pageSize) ?? DEFAULT_LISTING_PAGE_SIZE);

	const currentPageNumber = ref(1);

	const readManyQuery = computed<ReadManyQuery>(() => {
		const limit = computedPageSize.value;
		const offset = (currentPageNumber.value - 1) * limit;

		return {
			...processedSelection.value,
			filter: resolvedFilter.value,
			sort: computedQuery.value?.sort,
			limit,
			...(offset > 0 ? { offset } : {}),
		};
	});

	// we want to delay the network request until the throttled query has caught up with the current query
	const querySettled = computed(() => !toValue(throttle) || isDeepEqual(computedQuery.value, throttledQuery.value));

	const fetchPolicy = computed(() => {
		if (computedQuery.value?.filter && isRelationalFilter(computedQuery.value?.filter)) {
			// we can't locally filter a relational filter and have to rely on the server to do it for us
			return 'no-cache';
		}

		// query is settled (last fetch is long enough ago that we want to _actually_ fetch the data from the backend)
		if (querySettled.value) {
			return 'cache-and-fetch';
		}

		// rely on client only filtering, this will only return items that are already in the client cache
		return 'cache-only';
	});

	const collectionQuery = store.$collection(() => computedCollection.value?.apiName ?? '').query((q) => q.many(computedEnabled.value && currentWorkspaceSlug.value && filterReady.value
		? {
				fetchPolicy: fetchPolicy.value,
				// Keep paginated rows tied to the server response while still reading
				// live item values from rstore's cache.
				resultMode: 'responseRefs',
				filter: readManyQuery.value.filter,
				fields: readManyQuery.value.fields,
				include: readManyQuery.value.include,
				sort: readManyQuery.value.sort,
				textSearch: computedQuery.value?.search,
				pageIndex: 0,
				pageSize: computedPageSize.value,
			}
		: { enabled: false }));

	const isSettled = computed(() => !collectionQuery.loading.value && querySettled.value);

	const {
		items,
		totalItemsCount,
		currentItemsCount,
		isLoading,
	} = usePaginatedQuery({
		store,
		collectionQuery,
		pageNumber: currentPageNumber,
		pageSize: computedPageSize,
		collectionApiName: computed(() => computedCollection.value?.apiName),
		enabled: computedEnabled,
		resetOn: computedQuery,
	});

	const filteredItemsCount = computed(() => collectionQuery.meta.value?.filteredCount);

	const rawError = computed(() => collectionQuery.error.value ?? null);
	const isFetching = computed(() => collectionQuery.loading.value);

	// A transient failure stays hidden behind the spinner for a while, and once it is shown the
	// retries carry on behind it, so `error` is the auto-retry's own and not the raw one.
	const { isRetrying, error, reset: resetAutoRetry } = useAutoRetry({
		error: rawError,
		loading: isFetching,
		retry: () => collectionQuery.refresh(),
	});

	// A new query starts over: earlier failures must not count against it. Compare by value,
	// because the query is rebuilt into a new object on every recompute.
	watch(readManyQuery, (newVal, oldVal) => {
		if (isDeepEqual(newVal, oldVal)) return;
		resetAutoRetry();
	});

	/** Reloads the query. Explicit user intent, so the auto-retry starts over. */
	function refresh() {
		resetAutoRetry();

		return collectionQuery.refresh();
	}

	// RStore's cache-and-fetch path can expose an empty cache before the
	// background fetch has proven whether the result is actually empty.
	const showLoading = computed(() => {
		if (!computedEnabled.value || !computedCollection.value || !currentWorkspaceSlug.value) return false;
		if (items.value.length > 0) return false;
		// A shown failure wins over the retries still running behind it.
		if (error.value) return false;
		if (isRetrying.value) return true;
		if (isLoading.value || !isSettled.value) return true;
		return totalItemsCount.value == null && filteredItemsCount.value == null;
	});

	return {
		items,
		currentItemsCount,
		isLoading,
		isEnabled: computedEnabled,
		isSettled,
		showLoading,
		error,
		permissionError,
		pageNumber: currentPageNumber,
		totalItemsCount,
		filteredItemsCount,
		refresh,
		query: readManyQuery,
	};
}
